import { type RefObject, useEffect } from "react";

type Options = {
  /** Min intersection ratio (0-1) before the element counts as "in view". */
  threshold?: number;
  /** Ms of scroll inactivity required before the element counts as "settled". */
  settleDelayMs?: number;
  /**
   * Scroll container to observe visibility against. Defaults to the browser
   * viewport. Pass the nearest scrollable ancestor when the element lives
   * inside a windowed feed (e.g. a desktop chat panel) so intersection
   * reflects what the user actually sees.
   */
  root?: Element | null;
};

/**
 * Focuses the referenced element once it becomes visible AND scrolling has
 * settled. Runs once — disconnects after the first focus.
 *
 * Use this instead of `autoFocus` for inputs that mount below the fold or
 * during a programmatic scroll, where immediate focus would either steal
 * focus from the user's current view or fight the in-progress scroll.
 *
 * Nested scroll containers: scroll events do not bubble, but the window-level
 * listener below is attached with `capture: true` so it catches scroll events
 * from any descendant scroller during the capture phase. For intersection
 * specifically — which IS scoped to a `root` — pass `root` when the element
 * lives inside a container that doesn't fill the viewport.
 */
export function useFocusWhenInView(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  { threshold = 0.5, settleDelayMs = 150, root = null }: Options = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let isInView = false;
    let isScrolling = false;
    let scrollTimeout: number | undefined;

    const tryFocus = () => {
      if (isInView && !isScrolling) {
        // `preventScroll: true` — scroll-snap owns positioning in chatless;
        // focusing must never trigger a competing scroll adjustment.
        el.focus({ preventScroll: true });
        cleanup();
      }
    };

    const onScroll = () => {
      isScrolling = true;
      if (scrollTimeout !== undefined) window.clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        isScrolling = false;
        tryFocus();
      }, settleDelayMs);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        isInView = entries[0]?.isIntersecting ?? false;
        tryFocus();
      },
      { threshold, root },
    );

    const cleanup = () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll, true);
      if (scrollTimeout !== undefined) window.clearTimeout(scrollTimeout);
    };

    observer.observe(el);
    window.addEventListener("scroll", onScroll, true);

    return cleanup;
  }, [ref, enabled, threshold, settleDelayMs, root]);
}
